import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Ruler, Component, Zap, Box, HardHat, ClipboardCheck, Warehouse, Home, ArrowRight } from "lucide-react";
import TerraceriaModal from "./TerraceriaModal";

const services = [
  {
    icon: Ruler,
    title: "Diseño Arquitectónico",
    desc: "Diseño integral de espacios residenciales y comerciales con visión contemporánea.",
    span: "md:col-span-2 md:row-span-2",
    featured: true,
  },
  {
    icon: HardHat,
    title: "Construcción",
    desc: "Ejecución de obra con los más altos estándares de calidad.",
    span: "md:col-span-1",
    featured: false,
  },
  {
    icon: ClipboardCheck,
    title: "Gestoría & Administración",
    desc: "Servicios integrales con equipo especializado.",
    span: "md:col-span-1",
    featured: false,
    team: [
      "Directores responsables de obra",
      "Corresponsables estructurales",
      "Corresponsables en instalaciones",
    ],
  },
  {
    icon: Box,
    title: "Render 3D",
    desc: "Visualización fotorrealista antes de construir.",
    span: "md:col-span-1",
    featured: false,
  },
  {
    icon: Component,
    title: "Terracería",
    desc: "Preparación profesional de terrenos con maquinaria especializada y estudios de mecánica de suelos.",
    span: "md:col-span-1",
    featured: false,
    expandable: true,
  },
  {
    icon: Warehouse,
    title: "Naves Industriales",
    desc: "Diseño y construcción de espacios industriales a gran escala.",
    span: "md:col-span-1",
    featured: false,
  },
  {
    icon: Home,
    title: "Casa Habitación",
    desc: "Tu hogar soñado, diseñado a la medida de tu estilo de vida.",
    span: "md:col-span-1",
    featured: false,
  },
  {
    icon: Zap,
    title: "Instalaciones",
    desc: "Eléctricas, hidráulicas y sanitarias con tecnología de punta.",
    span: "md:col-span-2",
    featured: false,
  },
];

export default function ServicesBento() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [terraceriaOpen, setTerraceriaOpen] = useState(false);

  return (
    <section
      id="servicios"
      className="py-28 md:py-36 lg:py-44 px-6 md:px-12 lg:px-24 bg-[#F5F5F7] relative"
      data-testid="services-section"
    >
      <div className="max-w-[1800px] mx-auto relative z-10">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-20 mb-16 md:mb-24">
          <div className="md:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400"
            >
              Servicios
            </motion.p>
          </div>
          <div className="md:col-span-10">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-3xl md:text-5xl lg:text-6xl font-normal text-neutral-900 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Lo que hacemos
            </motion.h2>
          </div>
        </div>

        {/* Bento Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 50, scale: 0.97 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`${s.span} group relative overflow-hidden rounded-2xl border transition-all duration-500 ${
                s.expandable ? "cursor-pointer" : "cursor-default"
              } ${
                s.featured
                  ? "bg-[#0A0A0A] border-neutral-800 p-10 md:p-14"
                  : "bg-white border-neutral-100 hover:border-neutral-300 p-7 md:p-9 hover:shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
              }`}
              data-testid={`service-card-${i}`}
              onClick={s.expandable ? () => setTerraceriaOpen(true) : undefined}
            >
              {/* Hover glow for featured */}
              {s.featured && (
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/[0.03] rounded-full blur-3xl group-hover:bg-white/[0.06] transition-all duration-700" />
              )}

              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-all duration-500 ${
                s.featured
                  ? "bg-white/10 group-hover:bg-white/15"
                  : "bg-neutral-50 border border-neutral-100 group-hover:bg-black group-hover:border-black"
              }`}>
                <s.icon
                  size={18}
                  strokeWidth={1.5}
                  className={`transition-colors duration-500 ${
                    s.featured
                      ? "text-white"
                      : "text-neutral-700 group-hover:text-white"
                  }`}
                />
              </div>

              <h3
                className={`text-lg md:text-xl font-medium tracking-tight mb-2 ${
                  s.featured ? "text-white" : "text-neutral-900"
                }`}
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {s.title}
              </h3>
              <p className={`text-sm leading-relaxed font-light ${
                s.featured ? "text-neutral-400" : "text-neutral-500"
              }`}>
                {s.desc}
              </p>

              {/* Team list for Gestoría */}
              {s.team && (
                <ul className="mt-3 space-y-1.5">
                  {s.team.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-xs text-neutral-400 font-light">
                      <span className="w-1 h-1 rounded-full bg-neutral-300 mt-1.5 flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              )}

              {/* Expandable indicator */}
              {s.expandable && (
                <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-neutral-400 group-hover:text-neutral-900 transition-colors">
                  <span>Ver detalle</span>
                  <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </div>
              )}

              {s.featured && (
                <motion.div
                  initial={{ width: 0 }}
                  animate={inView ? { width: "40%" } : {}}
                  transition={{ duration: 1.2, delay: 0.8 }}
                  className="h-px bg-white/10 mt-8"
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Terracería detail modal */}
      <TerraceriaModal open={terraceriaOpen} onClose={() => setTerraceriaOpen(false)} />
    </section>
  );
}
